import { createMemo, createResource, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { PenLine, Sparkles, Users } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { staggerItem } from "@/lib/motion";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import {
  countByPostId,
  getFeedCommentsQuery,
  getFeedLikesQuery,
  getFeedQuery,
  likedPostIds,
  parseMedia,
  queryKeys,
  sortPostsByRecent,
  toggleLikeMutation,
} from "@famidex/shared";
import { fetchFeed } from "../data/community-rest";
import { type FeedPost, PostCard } from "./post-card";

/**
 * 📣 Explorar la comunidad — feed cronológico de publicaciones. Local-first para
 * el usuario autenticado (vista PowerSync reactiva del stream `community_feed`);
 * guest-first para el invitado (REST público `/api/community/feed`). Traducción
 * del diseño Stitch `explorar_comunidad`.
 */
export function CommunityFeedPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const { userId } = session;
  const isAuthed = () => session.isAuthenticated();

  // ── Camino local-first (usuario con cuenta) ────────────────────────────────
  const posts = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && isAuthed(),
    queryKey: () => queryKeys.community.feed(),
    queryFn: () => {
      const d = drizzle();
      return d ? getFeedQuery(d) : null;
    },
  });
  const likes = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && isAuthed(),
    queryKey: () => queryKeys.community.feedLikes(),
    queryFn: () => {
      const d = drizzle();
      return d ? getFeedLikesQuery(d) : null;
    },
  });
  const comments = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && isAuthed(),
    queryKey: () => queryKeys.community.comments(undefined),
    queryFn: () => {
      const d = drizzle();
      return d ? getFeedCommentsQuery(d) : null;
    },
  });

  // ── Camino guest (REST público) ────────────────────────────────────────────
  const [restFeed] = createResource(
    () => (isAuthed() ? null : "feed"),
    async () => fetchFeed(),
  );

  const likeCounts = createMemo(() => countByPostId(likes.data ?? []));
  const commentCounts = createMemo(() => countByPostId(comments.data ?? []));
  const likedSet = createMemo(() => likedPostIds(likes.data ?? [], userId()));

  interface Row {
    post: FeedPost;
    likeCount: number;
    commentCount: number;
    liked: boolean;
  }

  const rows = createMemo<Row[]>(() => {
    if (isAuthed()) {
      const sorted = sortPostsByRecent(posts.data ?? []);
      const counts = likeCounts();
      const cc = commentCounts();
      const liked = likedSet();
      return sorted.map((p) => ({
        post: {
          id: p.id,
          authorName: p.author_name,
          authorImage: p.author_image,
          caption: p.caption,
          productId: p.product_id,
          productName: null,
          media: parseMedia(p.media),
          rating: p.rating,
          createdAt: p.created_at,
        },
        likeCount: counts[p.id] ?? 0,
        commentCount: cc[p.id] ?? 0,
        liked: liked.has(p.id),
      }));
    }
    return (restFeed() ?? []).map((p) => ({
      post: {
        id: p.id,
        authorName: p.authorName ?? null,
        authorImage: p.authorImage ?? null,
        caption: p.caption ?? null,
        productId: p.productId ?? null,
        productName: p.productName ?? null,
        media: p.media,
        rating: p.rating ?? null,
        createdAt: p.createdAt,
      },
      likeCount: p.likeCount,
      commentCount: p.commentCount,
      liked: false,
    }));
  });

  const handleToggleLike = (postId: string) => {
    const d = drizzle();
    const uid = userId();
    if (!d || !uid || !isAuthed()) {
      return;
    }
    void toggleLikeMutation(d, uid, postId);
  };

  return (
    <div class="flex flex-col gap-6" data-testid="community-feed">
      <header class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-foreground">{t(msg`Explorar la comunidad`)}</h1>
          <p class="mt-1 text-sm text-muted-foreground">
            {t(msg`Fotos de sets y descubrimientos de coleccionistas`)}
          </p>
        </div>
        <Link
          to="/community/new"
          data-testid="community-new"
          class="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
        >
          <PenLine class="h-4 w-4" />
          {t(msg`Publicar`)}
        </Link>
      </header>

      <Show
        when={rows().length > 0}
        fallback={
          <div
            data-testid="community-empty"
            class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-16 text-center text-muted-foreground"
          >
            <Users class="h-8 w-8" />
            <p class="text-sm">{t(msg`Aún no hay publicaciones. ¡Sé el primero!`)}</p>
            <Link
              to="/community/new"
              class="mt-1 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
            >
              <Sparkles class="h-4 w-4" />
              {t(msg`Crear publicación`)}
            </Link>
          </div>
        }
      >
        <section class="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Feed con entrada escalonada GPU-only (lib/motion). */}
          <For each={rows()}>
            {(row, index) => (
              <div ref={(el: HTMLDivElement) => staggerItem(el, index())}>
                <PostCard
                  post={row.post}
                  likeCount={row.likeCount}
                  commentCount={row.commentCount}
                  liked={row.liked}
                  canInteract={isAuthed()}
                  onToggleLike={() => {
                    handleToggleLike(row.post.id);
                  }}
                />
              </div>
            )}
          </For>
        </section>
      </Show>
    </div>
  );
}
